// ═══════════════════════════════════════════════════════════════════════════
// evaluarElegibilidadPromo — Combina los 4 ejes en un veredicto por (item × campaña)
// ───────────────────────────────────────────────────────────────────────────
// Función pura que toma un item, una campaña ML y los datos de contexto, y
// devuelve qué tan "elegible para nosotros" es esa combinación. El resultado
// se usa para:
//
//   - Ordenar la tabla del detalle de campaña (sort por urgencia stock →
//     margen final neto desc).
//   - Pintar la fila con fondo rojo cuando es trampa (margen post negativo,
//     ACOS come margen, sin stock).
//   - Mostrar accionables sugeridos ("Pausá ads para entrar").
//   - KPIs de tier por campaña ("12 punta · 18 OK · 5 stock apretado · 8 ads
//     quemándote · 4 a pérdida").
//
// Ejes:
//   1. Stock     → urgencia (sobrado/ok/bajo/crítico/quebrado).
//   2. Margen    → margen sin ads pre y post-promo, con benchmark contextual.
//   3. Ads       → ACOS pre y post-promo (proyectado al nuevo precio).
//   4. Lift      → multiplicador de demanda del calendario comercial.
//
// La lógica del descuento sigue el modelo ML:
//   descuento_total          = original_price − precio_post
//   descuento_a_cargo_seller = descuento_total × seller_percentage / 100
//   descuento_a_cargo_ML     = descuento_total × meli_percentage / 100
//   precio_que_recibís_post  = original_price − descuento_a_cargo_seller
//                              (ML te bonifica su parte; vos perdés solo lo tuyo)
// ═══════════════════════════════════════════════════════════════════════════

import { clasificarUrgencia, scoreUrgencia } from '@core/lib/urgenciaStock'
import { obtenerBenchmarkMargen, obtenerBenchmarkAcos } from './benchmarks'
import { resolverLiftCampaña } from '@core/lib/eventosVenta'

// Tiers visuales para el sort y el fondo de la fila.
//   verde     → conviene entrar
//   amarillo  → evaluar (algún eje en alerta)
//   rojo      → no entrar (margen negativo, sin stock, ads quemándote)

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function diasEntreFechas(desde, hasta) {
  const d = new Date(desde).getTime()
  const h = new Date(hasta).getTime()
  if (!isFinite(d) || !isFinite(h) || h <= d) return null
  return Math.max(1, Math.round((h - d) / 86400000))
}

/**
 * @param {Object} opts
 * @param {Object} opts.item — mlPublicaciones doc (con _rentabilidadInferida + _costoAdquisicionInferido)
 * @param {Object} opts.producto — productos doc vinculado
 * @param {Object} opts.promoEnItem — mlPromosItems doc para esta combinación
 * @param {Object} opts.campania — mlPromos doc (master)
 * @param {Array}  opts.eventosCalendario — para resolver lift
 * @param {Array}  opts.publicacionesEmpresa — para fallback de benchmarks
 * @param {number} opts.velocidadDiaria — uds vendidas / día (calculado externo)
 * @param {number} opts.tiempoReposicionDias — del producto, default 30
 *
 * @returns {Object} veredicto completo
 */
export function evaluarElegibilidadPromo({
  item,
  producto,
  promoEnItem,
  campania,
  eventosCalendario = [],
  publicacionesEmpresa = null,
  velocidadDiaria = null,
  tiempoReposicionDias = null,
} = {}) {
  // ─── Inputs base ───
  const stock = num(item?.availableQuantity ?? item?.available_quantity) ?? 0
  const originalPrice = num(promoEnItem?.original_price) ?? num(item?.price) ?? null
  const sugDisc = num(promoEnItem?.suggested_discounted_price)
  const minDisc = num(promoEnItem?.min_discounted_price)
  const maxDisc = num(promoEnItem?.max_discounted_price)
  // Precio post-promo: usamos el suggested si existe, sino el price actual de la promo.
  // Para items ya started, el price es el aplicado real.
  const precioPost = sugDisc != null
    ? sugDisc
    : (num(promoEnItem?.price) ?? originalPrice)
  const sellerPct = num(promoEnItem?.seller_percentage) ?? 0
  const meliPct = num(promoEnItem?.meli_percentage) ?? 0
  const tipoCampania = campania?.type || promoEnItem?.promotionType || null

  // ─── Eje 1: Stock ───
  const repoDias = num(tiempoReposicionDias) ?? num(producto?.tiempoReposicionDias) ?? 30
  const cobertura = (velocidadDiaria != null && velocidadDiaria > 0)
    ? stock / velocidadDiaria
    : null  // null = velocidad indefinida → tratado como "infinito" en clasificarUrgencia
  const urgenciaStock = clasificarUrgencia(stock, cobertura, {
    modo: 'reposicion',
    tiempoReposicion: repoDias,
  })

  // ─── Eje 2: Margen sin ads ───
  // Usamos el benchmark del item (cascada) como margen "actual sin ads".
  // El margen post-promo se deriva descontando lo que paga el seller del
  // descuento total. Aproximación: trabajamos con el % sobre el precio post.
  const benchMargen = obtenerBenchmarkMargen({
    itemDoc: item,
    productoDoc: producto,
    publicacionesEmpresa,
  })
  const margenSinAdsActualPct = benchMargen.valor

  // Margen post-promo en $/u y %:
  //   margen_actual$ = original_price × margenActualPct
  //   descuento_a_cargo_seller$ = (original − precio_post) × sellerPct/100
  //   margen_post$ = margen_actual$ − descuento_a_cargo_seller$
  let margenSinAdsActual$ = null
  let margenSinAdsPost$ = null
  let margenSinAdsPostPct = null
  if (originalPrice != null && precioPost != null && precioPost > 0) {
    margenSinAdsActual$ = originalPrice * margenSinAdsActualPct
    const descuentoTotal$ = Math.max(0, originalPrice - precioPost)
    const descuentoSeller$ = descuentoTotal$ * (sellerPct / 100)
    margenSinAdsPost$ = margenSinAdsActual$ - descuentoSeller$
    margenSinAdsPostPct = margenSinAdsPost$ / precioPost
  }

  // ─── Eje 3: ACOS ───
  // Al bajar el precio, el ACOS sube proporcional (cost igual, revenue baja).
  const benchAcos = obtenerBenchmarkAcos({
    itemDoc: item,
    productoDoc: producto,
    publicacionesEmpresa,
  })
  const acosActualPct = benchAcos.valor
  let acosPostPct = null
  if (originalPrice != null && precioPost != null && precioPost > 0) {
    acosPostPct = acosActualPct * (originalPrice / precioPost)
  }

  // ─── Margen final neto ───
  // = margen sin ads post − ACOS post (ambos como %)
  // Es lo que le queda al seller después de descuento + ads.
  let margenFinalNetoPct = null
  let margenFinalNeto$ = null
  if (margenSinAdsPostPct != null && acosPostPct != null) {
    margenFinalNetoPct = margenSinAdsPostPct - acosPostPct
    if (precioPost != null) {
      margenFinalNeto$ = precioPost * margenFinalNetoPct
    }
  }

  // ─── Eje 4: Lift (acelerador) ───
  const liftRes = resolverLiftCampaña({
    campañaML: { name: campania?.name, type: tipoCampania },
    eventos: eventosCalendario,
  })

  // Unidades extra estimadas y ganancia extra
  let unidadesExtraEstimadas = null
  let gananciaExtraEstimada$ = null
  if (velocidadDiaria != null && velocidadDiaria > 0 && liftRes.lift > 1) {
    const duracionDias = diasEntreFechas(campania?.start_date, campania?.finish_date) || 7
    const velocidadPost = velocidadDiaria * liftRes.lift
    unidadesExtraEstimadas = Math.round((velocidadPost - velocidadDiaria) * duracionDias)
    if (margenFinalNeto$ != null) {
      gananciaExtraEstimada$ = unidadesExtraEstimadas * margenFinalNeto$
    }
  }

  // ─── Veredicto: tier + motivos + accionables ───
  const motivos = []
  const accionables = []
  let filaTier = 'verde'
  let excluido = false

  // 💀 Quebrado → excluir del listado
  if (urgenciaStock === 'quebrado') {
    filaTier = 'rojo'
    excluido = true
    motivos.push('Sin stock disponible')
    accionables.push('Reponer stock antes de promocionar')
  }

  // Margen sin ads post-promo negativo → fila roja, no entrar
  if (margenSinAdsPostPct != null && margenSinAdsPostPct < 0) {
    filaTier = 'rojo'
    motivos.push(`Margen post-promo negativo (${(margenSinAdsPostPct * 100).toFixed(1)}%)`)
    accionables.push('No entrar — el descuento solo no cierra')
  }

  // Pérdida con ads: margen sin ads post OK pero ads come todo → rojo, accionable
  if (margenSinAdsPostPct != null && margenSinAdsPostPct > 0
      && margenFinalNetoPct != null && margenFinalNetoPct <= 0) {
    filaTier = 'rojo'
    motivos.push(`Ads come todo el margen (ACOS post ${(acosPostPct * 100).toFixed(1)}% > margen ${(margenSinAdsPostPct * 100).toFixed(1)}%)`)
    accionables.push('Pausá ads para entrar — el producto solo es rentable')
  }

  // Stock crítico → warning (no excluye, pero baja prioridad)
  if (urgenciaStock === 'critico' && filaTier !== 'rojo') {
    filaTier = 'amarillo'
    motivos.push(`Stock crítico (cobertura ${cobertura?.toFixed(0) || '?'}d < reposición ${repoDias}d)`)
    accionables.push('Vas a quebrar antes de reponer — evaluar si vale el riesgo')
  }

  // Stock bajo → warning
  if (urgenciaStock === 'bajo' && filaTier === 'verde') {
    filaTier = 'amarillo'
    motivos.push('Stock apretado (cobertura entre 1×R y 1.5×R)')
  }

  // Margen post cae mucho respecto al actual (>60% de pérdida relativa)
  if (filaTier === 'verde'
      && margenSinAdsActualPct > 0
      && margenSinAdsPostPct != null
      && margenSinAdsPostPct / margenSinAdsActualPct < 0.4) {
    filaTier = 'amarillo'
    motivos.push(`Margen cae >60% del nivel actual (de ${(margenSinAdsActualPct * 100).toFixed(1)}% a ${(margenSinAdsPostPct * 100).toFixed(1)}%)`)
  }

  // ACOS alto: come 30-99% del margen → soft warning
  if (filaTier === 'verde'
      && margenSinAdsPostPct != null && margenSinAdsPostPct > 0
      && acosPostPct != null
      && acosPostPct / margenSinAdsPostPct >= 0.6
      && acosPostPct / margenSinAdsPostPct < 1) {
    filaTier = 'amarillo'
    motivos.push(`Ads come ${((acosPostPct / margenSinAdsPostPct) * 100).toFixed(0)}% del margen post`)
    accionables.push('Considerá pausar ads para mejorar margen')
  }

  // Bonus: si urgencia es Sobrado y margen es positivo, marcar como punta clara
  // (queda en verde, pero el motivo se muestra para que el asesor lo priorice)
  if (filaTier === 'verde' && urgenciaStock === 'sobrado' && margenFinalNetoPct > 0) {
    motivos.push('Stock sobrado + margen positivo — punta clara para promocionar')
  }

  return {
    // Eje 1: stock
    urgenciaStock,
    cobertura,
    tiempoReposicionDias: repoDias,
    velocidadDiaria,
    stock,

    // Eje 2: margen
    margenSinAdsActualPct,
    margenSinAdsActual$,
    margenSinAdsPostPct,
    margenSinAdsPost$,
    benchmarkMargen: benchMargen,

    // Eje 3: ads
    acosActualPct,
    acosPostPct,
    benchmarkAcos: benchAcos,

    // Eje 4: lift
    lift: liftRes.lift,
    liftFuente: liftRes.fuente,
    liftDetalle: liftRes.detalle,

    // Métricas finales
    originalPrice,
    precioPost,
    sellerPct,
    meliPct,
    sugDisc,
    minDisc,
    maxDisc,
    margenFinalNetoPct,
    margenFinalNeto$,
    unidadesExtraEstimadas,
    gananciaExtraEstimada$,

    // Veredicto
    filaTier,            // 'verde' | 'amarillo' | 'rojo'
    excluido,            // true solo cuando es quebrado (sin stock)
    motivos,             // array de strings
    accionables,         // array de strings
    scoreStock: scoreUrgencia(urgenciaStock),
  }
}

// ─── Helper para sort de filas (urgencia stock → margen final desc) ────────
//
// Devuelve un score numérico para ordenar filas. Mayor = más arriba.
// Combina urgencia (10000 unidades por bucket) + margen post (centavos).
//
// Quebrado y a-pérdida quedan AL FONDO (no excluyen, pero el sort los manda
// a la cola para que el verde quede arriba).

export function scoreParaSort(veredicto) {
  if (!veredicto) return 0
  // Tier inverso: verde=2, amarillo=1, rojo=0 → tier × 100000
  const tierBucket = veredicto.filaTier === 'verde' ? 200000
                  : veredicto.filaTier === 'amarillo' ? 100000
                  : 0
  // Urgencia: sobrado=4, ok=3, bajo=2, critico=1, quebrado=0 → ×10000
  const urgenciaBucket = veredicto.scoreStock * 100  // 0-10000
  // Margen final: lo sumamos como cents para que items con más margen suban
  const margenBucket = (veredicto.margenFinalNeto$ != null && isFinite(veredicto.margenFinalNeto$))
    ? Math.max(0, Math.min(10000, Math.round(veredicto.margenFinalNeto$ / 100)))
    : 0
  return tierBucket + urgenciaBucket + margenBucket
}
